import { ref, computed, watch } from 'vue'
import useEventEmitter from '@/_core/hooks/useEventEmitter'
import { saveTaskProgress } from '@/services/vpr'
import useApiRequest from '@/_core/hooks/useApiRequest'
import store from '@/store'
import {
  PLAYER_STATE_STATUS_HALF,
  PLAYER_STATE_STATUS_NONE,
  PLAYER_STATE_STATUS_SUCCESS,
  PLAYER_STATE_STATUS_WITH_ERROR,
  VPR_TASK_RESULT_INVALID,
  VPR_TASK_RESULT_NONE,
  VPR_TASK_RESULT_PART,
  VPR_TASK_RESULT_VALID
} from '@/config'

const PLAYER_MODE_CONTROL = 'controlling'
// const PLAYER_MODE_RESULTS = 'controlled'

/**
 * @param  {Function} ChildVprController - ChildVprController controller instance
 * @param  {ComputedRefImpl} taskId - ref taskId
 */
export default function PlayerTaskController (ChildVprController, taskId) {
  if (!ChildVprController) throw new Error('ChildVprController instance is not defined')
  if (!taskId.value) throw new Error('Task ID is not defined')

  const { $on, $off, $emit } = useEventEmitter()

  const task = computed(() => ChildVprController.childVpr.value?.userItem.vpr.findTaskById(taskId.value) ?? null)

  const initialPlayerState = computed(() => task.value?.progress?.playerState ?? null)

  const currentState = ref(null)
  const _setCurrentState = val => { currentState.value = val }

  // request task state
  watch(taskId, id => {
    _setCurrentState(null)
    ChildVprController.preloadTaskStateRequest(task.value)
  }, { immediate: true })

  const _mode = computed(() => PLAYER_MODE_CONTROL)

  // Iframe Listener logic
  const iframeElementRef = ref(null)
  const initIframeListener = (iframeRef) => {
    iframeElementRef.value = iframeRef
    window.addEventListener('message', _onMessage)
  }
  const destroyIframeListener = () => {
    iframeElementRef.value = null
    _setCurrentState(null)
    window.removeEventListener('message', _onMessage)
  }

  const correlationId = ref(null)
  const setCorrelationId = val => { correlationId.value = val }

  const _onMessage = message => {
    try {
      if (!message?.data) return
      if (!iframeElementRef.value) { console.warn('iframeElementRef is not initialized'); return }
      const parsedData = JSON.parse(message.data)

      switch (parsedData.msgType) {
        case 'eor:container-ready':
          setCorrelationId(parsedData.correlationId)
          if (initialPlayerState.value) { _openState(initialPlayerState.value) }
          break

        case 'eor:user:state-checked':
          // На завершение теста
          _getState()
          // Send POST request - save state
          if (_mode.value === PLAYER_MODE_CONTROL) {
            setTimeout(() => {
              _requestSaveProgress()
            }, 500)
          }
          break

        case 'eor:state':
          // Сохранить State
          _setCurrentState(parsedData.payload)
          break

        case 'eor:click':
          switch (parsedData.name) {
            case 'close':
              $emit('iframe-close-click')
              break
          }
          break

        default:
          break
      }
    } catch (error) {
      console.warn('Error in message:', error)
    }
  }

  const _openState = (payload) => {
    const message = {
      correlationId: correlationId.value,
      msgType: 'eor:open',
      href: task.value.sourcesPlayerUrl,
      state: {
        payload: payload
      }
    }
    _postMessage(message)
  }

  const _postMessage = message => {
    iframeElementRef.value.contentWindow.postMessage(JSON.stringify(message), '*')
  }

  const _getState = () => {
    const message = {
      correlationId: correlationId.value,
      msgType: 'eor:get-state'
    }
    _postMessage(message)
  }

  // Save state logic
  const childId = computed(() => store.user.selectedChild.id)

  const saveProgressRequest = useApiRequest(saveTaskProgress)
  saveProgressRequest.$on('success', () => {
    ChildVprController.childVpr.value.setTaskProgress(saveProgressRequest.data)
    $emit('requestDoneTasks-success', saveProgressRequest.data)
    _setCurrentState(null)
  })
  saveProgressRequest.$on('error', () => {
    $emit('requestDoneTasks-error', saveProgressRequest.errors)
  })
  const _requestSaveProgress = async () => {
    const data = {
      status: defineStateStatus(currentState.value),
      player_state: JSON.stringify(currentState.value)
    }
    saveProgressRequest.request(
      childId.value,
      ChildVprController.childVpr.value.userItem.id,
      taskId.value,
      data
    )
  }

  // Output.
  return {
    task,
    initIframeListener,
    destroyIframeListener,
    $on,
    $off
  }
}

function defineStateStatus (payload) {
  const extensions = payload?.statement?.result?.extensions ?? {}
  const extensionProps = Object.keys(extensions)
  const extensionName = extensionProps.length ? extensionProps[0] : '0000/result'

  const extensionsObjs = payload?.statement?.result?.extensions?.[extensionName]?.results ?? {}

  let status = null
  // first object with status
  Object.values(extensionsObjs).some((playerTaskObject) => {
    status = playerTaskObject.state ?? null
    return status
  })

  switch (status) {
    default:
    case PLAYER_STATE_STATUS_NONE: return VPR_TASK_RESULT_NONE
    case PLAYER_STATE_STATUS_SUCCESS: return VPR_TASK_RESULT_VALID
    case PLAYER_STATE_STATUS_WITH_ERROR: return VPR_TASK_RESULT_INVALID
    case PLAYER_STATE_STATUS_HALF: return VPR_TASK_RESULT_PART
  }
}
